import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { RefreshCcw } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Button } from '@/components/ui/Button';
import {
  useCreateEvent,
  useUpdateEvent,
  useEvent,
  useIsEventCodeAvailable,
} from '@/hooks/useEvents';
import { useAuth } from '@/hooks/useAuth';
import { generateEventCode } from '@/lib/api/events';
import { EVENT_COLORS, EVENT_COLOR_LABEL, eventColorClasses, isEventColor } from '@/lib/eventColors';
import { cn, formatDateTime } from '@/lib/format';

export interface EventFormProps {
  open: boolean;
  onClose: () => void;
  /**
   * Id of the event being edited. When set the form loads that event and saves
   * with updateEvent instead of creating a new one.
   *
   * Note the code field is read-only while editing. The code is stamped onto
   * every allocation and printed on invoices; renaming it after the fact would
   * leave those pointing at something that no longer exists. Name, location,
   * dates and colour are safe to correct — the database refuses even those once
   * the event is closed (guard_event_edit, migration 0015).
   */
  editingEventId?: string;
}

interface FormValues {
  name: string;
  code: string;
  location: string;
  /** Palette token or '' for none. */
  color: string;
  /** datetime-local strings; '' = unset. */
  startAt: string;
  endAt: string;
}

const CODE_PATTERN = /^[A-Z0-9-]+$/;

/** ms → value for <input type="datetime-local"> in the browser's local time. */
function msToLocalInput(ms?: number): string {
  if (!ms) return '';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
const localInputToMs = (v: string): number | undefined => (v ? new Date(v).getTime() : undefined);

export function EventForm({ open, onClose, editingEventId }: EventFormProps) {
  const { user } = useAuth();
  const createEvent = useCreateEvent();
  const updateEvent = useUpdateEvent();
  const isEditing = !!editingEventId;
  const { data: editing, isLoading: loadingEvent } = useEvent(editingEventId ?? null);
  const [codeTouched, setCodeTouched] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { name: '', code: '', location: '', color: '', startAt: '', endAt: '' },
  });

  const codeValue = watch('code');
  const colorValue = watch('color');

  useEffect(() => {
    if (!open) return;
    setSaveError(null);
    setCodeTouched(false);
    if (isEditing) {
      // Wait for the fetch; resetting to blanks first would flash an empty form
      // over the values being edited.
      if (editing) {
        reset({
          name: editing.name,
          code: editing.code,
          location: editing.location ?? '',
          color: editing.color ?? '',
          startAt: msToLocalInput(editing.startAt),
          endAt: msToLocalInput(editing.endAt),
        });
      }
    } else {
      reset({
        name: '',
        code: generateEventCode(),
        location: '',
        color: '',
        startAt: msToLocalInput(Date.now()),
        endAt: '',
      });
    }
  }, [open, isEditing, editing, reset]);

  const { data: codeAvailable, isFetching: codeChecking } = useIsEventCodeAvailable(
    codeValue,
    editingEventId,
  );

  const codeError = useMemo<string | undefined>(() => {
    if (!codeValue) return undefined;
    if (!CODE_PATTERN.test(codeValue)) return 'Hanya huruf besar, angka, dan dash';
    if (!codeChecking && codeAvailable === false) return 'Code sudah dipakai event lain';
    return undefined;
  }, [codeValue, codeAvailable, codeChecking]);

  const handleRegenerateCode = () => {
    setValue('code', generateEventCode(), { shouldValidate: true, shouldDirty: true });
    setCodeTouched(true);
  };

  const onSubmit = async (values: FormValues) => {
    if (!user) return;
    setSaveError(null);
    const startAt = localInputToMs(values.startAt);
    const endAt = localInputToMs(values.endAt);
    if (startAt && endAt && endAt < startAt) {
      setSaveError('Tanggal selesai tidak boleh sebelum tanggal mulai.');
      return;
    }
    const color = isEventColor(values.color) ? values.color : null;
    try {
      if (isEditing) {
        // Code is deliberately not sent — it is not editable here.
        await updateEvent.mutateAsync({
          id: editingEventId!,
          input: {
            name: values.name.trim(),
            location: values.location.trim() || null,
            color,
            ...(startAt ? { startAt } : {}),
            endAt: endAt ?? null,
          },
        });
      } else {
        if (codeError) return;
        if (codeAvailable === false) return;
        await createEvent.mutateAsync({
          name: values.name.trim(),
          code: values.code.trim(),
          location: values.location.trim() || undefined,
          color,
          startAt,
          endAt,
          createdBy: user.uid,
        });
      }
      onClose();
    } catch (e) {
      // guard_event_edit refuses a closed or archived event. Its message names
      // the event and its state, which is what the person needs to act.
      setSaveError(e instanceof Error ? e.message : 'Gagal menyimpan event');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit event' : 'Start new event'}
      description={
        isEditing
          ? 'Perbaiki nama, lokasi, jadwal atau warna. Kode event tidak bisa diubah — kode itu tercetak di invoice yang sudah terbit.'
          : 'Event adalah sesi penjualan — biasanya satu bazaar/expo. Bisa multiple concurrent.'
      }
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="event-form"
            variant="primary"
            loading={isSubmitting || createEvent.isPending || updateEvent.isPending}
            disabled={isEditing ? loadingEvent : !!codeError || codeChecking}
          >
            {isEditing ? 'Simpan perubahan' : 'Start event'}
          </Button>
        </>
      }
    >
      <form id="event-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {saveError && (
          <div role="alert" className="p-3 rounded border border-danger/30 bg-danger/10 text-small text-danger">
            {saveError}
          </div>
        )}
        <Input
          label="Event name"
          placeholder="Contoh: PERDAMI 2026"
          hint="Ditampilkan di TopBar dan invoice."
          error={errors.name?.message}
          autoFocus
          {...register('name', {
            required: 'Nama event wajib diisi',
            minLength: { value: 2, message: 'Minimal 2 karakter' },
            maxLength: { value: 80, message: 'Maksimal 80 karakter' },
          })}
        />

        <div>
          <Input
            label="Event code"
            placeholder="EVT-20260609-7K2P"
            readOnly={isEditing}
            hint={
              isEditing
                ? 'Tidak bisa diubah — kode ini tercetak di invoice yang sudah terbit.'
                : 'Identifier unik. Otomatis di-generate, boleh diedit.'
            }
            error={isEditing ? undefined : (codeError ?? errors.code?.message)}
            {...register('code', {
              required: 'Code wajib diisi',
              minLength: { value: 3, message: 'Minimal 3 karakter' },
              maxLength: { value: 32, message: 'Maksimal 32 karakter' },
              onChange: () => setCodeTouched(true),
            })}
          />
          <div className="mt-2 flex items-center justify-between">
            <p className="text-caption text-ink-subtle">
              {isEditing ? 'Terkunci' : codeTouched ? 'Disimpan apa adanya.' : 'Otomatis · ' + formatDateTime(Date.now())}
            </p>
            {!isEditing && (
              <button
                type="button"
                onClick={handleRegenerateCode}
                className="inline-flex items-center gap-1 text-caption text-primary hover:text-primary-hover font-medium"
              >
                <RefreshCcw size={12} />
                Generate ulang
              </button>
            )}
          </div>
        </div>

        {/* Colour: a token, so the card, the TopBar chip and the POS header all
            agree, and a cashier running two events can tell them apart. */}
        <div>
          <p className="block text-small font-medium text-ink mb-1.5">Warna penanda</p>
          <div role="radiogroup" aria-label="Warna event" className="flex flex-wrap gap-2">
            <button
              type="button"
              role="radio"
              aria-checked={colorValue === ''}
              onClick={() => setValue('color', '', { shouldDirty: true })}
              className={cn(
                'px-2.5 py-1 rounded border text-caption transition-colors',
                colorValue === '' ? 'border-primary bg-primary/5 text-ink' : 'border-line text-ink-subtle hover:text-ink',
              )}
            >
              Tanpa warna
            </button>
            {EVENT_COLORS.map((t) => (
              <button
                key={t}
                type="button"
                role="radio"
                aria-checked={colorValue === t}
                title={EVENT_COLOR_LABEL[t]}
                aria-label={EVENT_COLOR_LABEL[t]}
                onClick={() => setValue('color', t, { shouldDirty: true })}
                className={cn(
                  'w-8 h-8 rounded-full inline-flex items-center justify-center transition-transform',
                  eventColorClasses(t).dot,
                  colorValue === t ? 'ring-2 ring-offset-2 ring-primary scale-110' : 'hover:scale-105',
                )}
              >
                {colorValue === t && <span className="w-2 h-2 rounded-full bg-white" />}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Input
            label="Mulai"
            type="datetime-local"
            error={errors.startAt?.message}
            {...register('startAt', { required: 'Tanggal mulai wajib diisi' })}
          />
          <Input
            label="Selesai (opsional)"
            type="datetime-local"
            hint="Dicatat otomatis saat event ditutup kalau dikosongkan."
            {...register('endAt')}
          />
        </div>

        <Textarea
          label="Lokasi (opsional)"
          placeholder="Contoh: Hall B, JCC Senayan"
          rows={2}
          hint="Hanya catatan — tidak dipakai di invoice saat ini."
          {...register('location', { maxLength: { value: 200, message: 'Maksimal 200 karakter' } })}
        />
      </form>
    </Modal>
  );
}
